import { useCallback, useEffect, useRef, useState } from 'react';
import { useLazyQuery } from '@apollo/client';
import { dealBuilderApi, InventoryEquipmentItem, InventoryFullListResponse } from '../api';

export const useEquipmentSearch = () => {
  const [results, setResults] = useState<InventoryEquipmentItem[]>([]);
  const [isActive, setIsActive] = useState(false);
  const [isSearching, setIsSearching] = useState(false);
  const [searchInventory] = useLazyQuery<InventoryFullListResponse>(dealBuilderApi.getInventoryFullList, {
    fetchPolicy: 'network-only',
  });
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Only the response of the latest search may update the results
  const requestIdRef = useRef(0);

  useEffect(
    () => () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
      requestIdRef.current += 1;
    },
    []
  );

  const search = useCallback(
    (term: string) => {
      if (debounceRef.current) clearTimeout(debounceRef.current);

      const requestId = ++requestIdRef.current;
      const trimmed = term.trim();

      if (!trimmed) {
        setResults([]);
        setIsActive(false);
        setIsSearching(false);
        return;
      }

      setIsActive(true);
      setIsSearching(true);

      debounceRef.current = setTimeout(async () => {
        try {
          const { data, error } = await searchInventory({
            variables: { input: { search: trimmed.toLowerCase() } },
          });

          if (requestId !== requestIdRef.current) return;
          if (!error) setResults(data?.result?.list ?? []);
        } finally {
          if (requestId === requestIdRef.current) setIsSearching(false);
        }
      }, 300);
    },
    [searchInventory]
  );

  return { search, results, isActive, isSearching };
};
